import { makeModuleStateSelector } from "@client/content/selectors";
import { getMultiselectValue } from "@client/lib/settings";
import { TargetSelector, combinedSettingsSelector } from "@client/selectors";
import appInfo from "@common/appInfo";
import { createSelector } from "reselect";
import { defaultState, previewState } from "./reducer";
import { type Kpi } from "./types";

export const localStateSelector = makeModuleStateSelector<Kpi.State>(defaultState, previewState);

export const makeAggregateRequestSelector = () => {
  const settingsSelector = combinedSettingsSelector(TargetSelector.Entity, [
    "range",
    "rangeUnit",
    "kpi",
    "genealogyType",
    { name: "segment", converter: getMultiselectValue, prop: "segment" },
    "volume",
    { name: "volumeType", converter: getMultiselectValue, prop: "volumeType" },
    "startBreakawayLevel",
    "endBreakawayLevel",
    "startDifferentialLevel",
    "endDifferentialLevel",
    "startLeg",
    "endLeg",
    "startLevel",
    "endLevel",
    "startRank",
    "endRank",
    "pin"
  ]);

  return createSelector([settingsSelector], (settings): Kpi.Api.GetAggregateRequest | null => {
    if (!settings.kpi) return null;

    const segment = settings.segment ?? [];
    const volumeType = settings.volumeType ?? [];
    return {
      kpi: settings.kpi,
      genealogyTypeId: settings.kpi === "NetworkVolume" ? settings.genealogyType || null : null, // empty string -> null
      currencyCode: appInfo.currencyCode,
      endBreakawayLevel: segment.includes("BreakawayLevel") ? settings.endBreakawayLevel : null,
      startBreakawayLevel: segment.includes("BreakawayLevel") ? settings.startBreakawayLevel : null,
      endDifferentialLevel: segment.includes("DifferentialLevel") ? settings.endDifferentialLevel : null,
      startDifferentialLevel: segment.includes("DifferentialLevel") ? settings.startDifferentialLevel : null,
      endLeg: segment.includes("Leg") ? settings.endLeg : null,
      startLeg: segment.includes("Leg") ? settings.startLeg : null,
      endLevel: segment.includes("Level") ? settings.endLevel : null,
      startLevel: segment.includes("Level") ? settings.startLevel : null,
      startRankId: segment.includes("Rank") ? settings.startRank : null,
      endRankId: segment.includes("Rank") ? settings.endRank : null,
      isBanked:
        (volumeType.includes("Banked") && volumeType.includes("Unbanked")) ||
        (!volumeType.includes("Banked") && !volumeType.includes("Unbanked"))
          ? null
          : volumeType.includes("Banked")
          ? true
          : false,
      isPersonal:
        (volumeType.includes("PersonalRecruit") && volumeType.includes("TeamRecruit")) ||
        (!volumeType.includes("PersonalRecruit") && !volumeType.includes("TeamRecruit"))
          ? null
          : volumeType.includes("PersonalRecruit")
          ? true
          : false,
      isPin: settings.kpi === "NetworkVolume" ? null : settings.pin,
      range: settings.range,
      rangeUnit: settings.rangeUnit,
      volumeType: settings.kpi === "NetworkVolume" ? settings.volume : null
    };
  });
};

export const makeReloadHashSelector = () => {
  const requestSelector = makeAggregateRequestSelector();

  return createSelector([requestSelector], (request) => {
    if (!request) return "";

    return JSON.stringify(request);
  });
};
